import * as React from "react";
import { Modal, Table, Input, Button, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getRewardListApi, type RewardListItem } from "src/api/reward/rewardApi";
import type { RewardItemDetail } from "src/types/reward";
import { snackbarStore } from "src/store/snackbarStore";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (reward: RewardListItem) => void;
};

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

const getRewardTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    POINT: "포인트",
    COUPON: "쿠폰",
    PRODUCT: "상품",
  };
  return labels[type] || type;
};

const itemSummary = (item: RewardItemDetail) => {
  const base =
    item.rewardType === "POINT"
      ? `${(item.pointAmount ?? 0).toLocaleString()}P`
      : item.couponName || `쿠폰 #${item.couponId ?? "-"}`;
  return item.quantity && item.quantity > 1 ? `${base} x${item.quantity}` : base;
};

export default function RewardSelectModal({ open, onClose, onSelect }: Props) {
  const [loading, setLoading] = React.useState(false);
  const [rewards, setRewards] = React.useState<RewardListItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [searchText, setSearchText] = React.useState("");

  const fetchRewards = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getRewardListApi({
        page,
        size: pageSize,
      });

      if (res.statusCode === 1 && res.data) {
        setRewards(res.data.content);
        setTotal(res.data.page.totalElements);
      } else {
        toast(res.message || "리워드 목록을 불러올 수 없습니다.", "error");
      }
    } catch (e: any) {
      toast(e?.message || "리워드 목록을 불러오는 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  React.useEffect(() => {
    if (open) {
      fetchRewards();
    } else {
      setSearchText("");
      setPage(0);
    }
  }, [open, fetchRewards]);

  const filteredRewards = React.useMemo(() => {
    if (!searchText) return rewards;
    const lowerSearch = searchText.toLowerCase();
    return rewards.filter(
      (reward) =>
        reward.rewardName.toLowerCase().includes(lowerSearch) ||
        reward.rewardId.toString().includes(lowerSearch)
    );
  }, [rewards, searchText]);

  const handleSelect = (reward: RewardListItem) => {
    onSelect(reward);
    onClose();
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "rewardId",
      width: 80,
    },
    {
      title: "리워드명",
      dataIndex: "rewardName",
      width: 200,
    },
    {
      title: "구성",
      dataIndex: "items",
      width: 110,
      render: (items: RewardItemDetail[]) => {
        const count = items?.length ?? 0;
        if (count === 0) return "-";
        return count > 1 ? (
          <Tag color="purple">쿠폰팩 ({count}개)</Tag>
        ) : (
          <Tag color="blue">{getRewardTypeLabel(items[0].rewardType)}</Tag>
        );
      },
    },
    {
      title: "보상 항목",
      dataIndex: "items",
      width: 220,
      render: (items: RewardItemDetail[]) =>
        items && items.length > 0 ? items.map(itemSummary).join(", ") : "-",
    },
    {
      title: "상태",
      dataIndex: "isActive",
      width: 80,
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? "#52c41a" : "#999" }}>{isActive ? "활성" : "비활성"}</span>
      ),
    },
    {
      title: "액션",
      width: 100,
      align: "center" as const,
      fixed: "right" as const,
      render: (_: any, record: RewardListItem) => (
        <Button type="primary" size="small" onClick={() => handleSelect(record)}>
          선택
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="리워드 선택"
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="리워드 ID, 이름으로 검색"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredRewards}
        rowKey="rewardId"
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p - 1);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개`,
        }}
        size="small"
        scroll={{ x: 1000 }}
      />
    </Modal>
  );
}
